// src/lib/axiosClient.ts
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosHeaders, AxiosError } from 'axios';
import { queryClient } from '@/lib/queryClient';
import { useAuthStore } from '@/store/auth';

interface AxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://dev.home.quizplease.ru';
const axiosClient: AxiosInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
});

axiosClient.interceptors.request.use(
  (config) => {
    const headers =
      config.headers instanceof AxiosHeaders
        ? config.headers
        : AxiosHeaders.from(config.headers || {});
    const token = useAuthStore.getState().token;
    if (token) headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
    return config;
  },
  (error) => Promise.reject(error),
);

axiosClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalReq = error.config as AxiosRequestConfigWithRetry;

    const is401 = error.response?.status === 401;
    const isRefreshCall = originalReq.url?.endsWith('/api/auth/refresh');

    if (is401 && !originalReq._retry && !isRefreshCall) {
      originalReq._retry = true;
      try {
        const { data } = await axiosClient.post('/api/auth/refresh');
        const newToken = data.token;

        useAuthStore.getState().setToken(newToken);
        axiosClient.defaults.headers.common.Authorization = `Bearer ${newToken}`;

        const updatedHeaders = AxiosHeaders.from(originalReq.headers);
        updatedHeaders.set('Authorization', `Bearer ${newToken}`);
        originalReq.headers = updatedHeaders;

        return axiosClient(originalReq);
      } catch (refreshError) {
        useAuthStore.getState().clearAuth();
        delete axiosClient.defaults.headers.common.Authorization;
        queryClient.setQueryData(['/api/user'], null);
        queryClient.clear();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosClient;
